import React, {useCallback, Fragment} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {trackPromise} from "react-promise-tracker";
import {usePromiseTracker} from "react-promise-tracker";
import {Subject} from "rxjs";
import _ from "lodash";
import {useObservable} from "../../../rxsupport";
import {ReferralInquiryInput} from "../domain";
import {useLatestReferralInquiryInput} from "./useLatestReferralInquiryInput";
import {useReferralInquiry} from "./useReferralInquiry";
import {ModalActionPayload} from "../../modal/action";
import {CloseButton, LoadingSpinner, showToast} from "../../../common";
import {FatOutlineButton} from "../../../common/component/FatOutlineButton";
import {ModalTextAreaInput} from "../../client/form-components/ModalTextAreaInput";
import {formatDateStr} from '../../../common/util/date';

type Props = {
    visible$: Subject<boolean>;
    referralGuid: string;
    dispatch: React.Dispatch<ModalActionPayload>;
    readOnly?: boolean;
    submittedDate?: string | null;
    onSubmitted?: () => void;
};

export function ReferralInquiryFormModal({visible$, referralGuid, dispatch, readOnly = false, submittedDate, onSubmitted}: Props) {
    const visible = useObservable(visible$, false);
    const {promiseInProgress} = usePromiseTracker();

    const methods = useForm<ReferralInquiryInput>({
        defaultValues: {
            referralInquiry: ""
        }
    });

    const {submitReferralInquiry} = useReferralInquiry(referralGuid);
    const {isRunning$, error} = useLatestReferralInquiryInput(visible, methods, referralGuid, readOnly)
    const loading = useObservable(isRunning$, false);
    if (error) {
        console.error('Error fetching referral inquiry:', error);
    }

    const isCallingBackend = loading || promiseInProgress;

    
    const submit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        await methods.handleSubmit(async (data: ReferralInquiryInput) => {
            await trackPromise(submitReferralInquiry(_.pick(data, [
                'referralInquiry',
            ])));
            methods.reset();
            visible$.next(false);
            dispatch({
                type: 'MODAL_ACTION',
                show: false,
            });
            onSubmitted?.();
        })(e);
    }, [methods, visible$, submitReferralInquiry, dispatch, onSubmitted]);

    const close = useCallback(
        () => {
            showToast.warning(`Submission for Referral Inquiry is cancelled`);
            methods.reset();
            visible$.next(false);
        },
        [visible$, methods]
    );

    return (
        <div className={`overflow-y-auto overflow-x-hidden fixed inset-0 z-50 flex justify-center items-center ${visible ? '' : 'hidden'}`}
             style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
            <div className="relative px-4 w-full max-w-2xl h-full md:h-auto">
                <div className="relative bg-white rounded-lg shadow-xl">
                    <FormProvider {...methods}>
                        <form autoComplete="off" onSubmit={submit}>
                            {/* Header */}
                            <div className="flex justify-between items-center p-4 border-b">
                                <h3 className="text-lg font-semibold">
                                    Referral Inquiry
                                </h3>
                                <CloseButton onClick={close} />
                            </div>

                            {!isCallingBackend && (
                                <Fragment>
                                    <div className={"p-2"}/>
                                    {/* Body */}
                                    <div className="p-6">
                                        <ModalTextAreaInput
                                            disabled={isCallingBackend || readOnly}
                                            field="referralInquiry"
                                            label="Please describe your referral inquiry"
                                            placeholder="Enter your referral inquiry..."
                                            labelPosition="left"
                                            rowsNum={5}
                                            maxLength={4000}
                                            required={!readOnly}
                                            requiredMessage="Please provide a description of the referral inquiry before submitting."
                                            showCharacterCount
                                            shouldStopInputOnMaxLength={false}
                                        />
                                    </div>
                                    { readOnly && (
                                        <div className="mx-6 mt-4 px-4 py-3 rounded-md bg-yellow-50 border border-yellow-300 text-sm text-yellow-800 mb-4">
                                            A referral inquiry has been submitted for this referral
                                            { submittedDate && <span> on <strong>{ formatDateStr(submittedDate) }</strong></span> } and is still being processed.
                                            {' '}If you need further assistance, please reach out to{' '}
                                            <a href="mailto:clientplacement@catalight.org"
                                               className="font-medium underline hover:text-yellow-900">
                                                clientplacement@catalight.org
                                            </a>.
                                        </div>
                                    ) }

                                    {/* Footer */}
                                    <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                                        <div className="flex w-1/3">
                                            <FatOutlineButton color={'red'}
                                                              text={"Cancel"}
                                                              onClick={close}/>
                                            { !readOnly && (
                                                <>
                                                    <span style={{width: 10}}/>
                                                    <FatOutlineButton type="submit" text={"Submit"} color={"green"}/>
                                                </>
                                            ) }
                                        </div>
                                    </div>
                                </Fragment>
                            )}

                            {isCallingBackend &&
                                <div className="p-24">
                                    <LoadingSpinner text="Loading"/>
                                </div>
                            }
                        </form>
                    </FormProvider>
                </div>
            </div>
        </div>
    );
}
